import { StrapiMedia } from "../../../types/strapiTypesUtility";
import Image from "next/image";

const BASE_URL = process.env.NEXT_PUBLIC_STRAPI_API_URL || 'https://strapi.pelgulinnakool.ee';

export type ImageQuality = 'thumbnail' | 'small' | 'medium' | 'large' | 'original' | (string & {});

interface Props extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'width' | 'height'> {
    media: StrapiMedia | null;
    quality?: ImageQuality;
}

export function StrapiImage({ media, quality = "medium", className, ...props }: Props) {
    if (!media?.url) return null;

    // 1. Validate requested quality; default to 'medium' if invalid
    const validQualities = ['thumbnail', 'small', 'medium', 'large', 'original'];
    const targetQuality = validQualities.includes(quality) ? quality : 'medium';

    // 2. Select requested format from formats object
    let selectedFormat: { url: string; width: number; height: number } | undefined;

    if (targetQuality !== 'original' && media.formats) {
        selectedFormat = media.formats[targetQuality as keyof typeof media.formats];

        // If requested format doesn't exist in Strapi (e.g. small original image), fallback to 'medium'
        if (!selectedFormat && targetQuality !== 'medium') {
            selectedFormat = media.formats.medium;
        }
    }

    // 3. Fallback to original root media if selected format is unavailable
    const imgUrl = selectedFormat?.url || media.url;
    const imgWidth = selectedFormat?.width || media.width;
    const imgHeight = selectedFormat?.height || media.height;

    if (!imgUrl || !imgWidth || !imgHeight) return null;

    const fullUrl = imgUrl.startsWith("http") ? imgUrl : `${BASE_URL}${imgUrl}`;

    return (
        <Image
            src={fullUrl}
            width={imgWidth}
            height={imgHeight}
            alt={media.alternativeText || props.alt || ""}
            className={className}
            {...props}
        />
    );
}